import { useAtom } from "@dbeining/react-atom";
import { Form as FormikForm, Formik, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject, type Ref } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { EditFormSaveCancelButtons, EditFormSplitFooter } from "../../components/Forms";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ParentEntityLifecycleBanner } from "../../components/Layout/ParentEntityLifecycleBanner";
import { userAtom } from "../../atoms";
import type { IRole } from "../../customTypings/user/Role";
import { resolveWasteMovementFormAccess } from "@/features/wasteMovement/core/wasteMovementFormAccess";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import {
    WasteMovementFormGrid,
    applyEwcDefaultRule,
    createEwcRuleFieldState,
    filterReportingEligibleSites,
    markFieldUserLocked,
    mergeWasteMovementSiteDefaultsIntoFormValues,
    resolveNewWasteMovementSiteId,
    useWasteMovementReferenceLookups,
    useWasteMovementCarrierLookups,
    DwtSubmissionSection,
    wasteMovementDirectionLabel,
    buildWasteMovementValidationSchema,
    buildReceiptCapturePayload,
    buildHazardousPopsPayload,
    buildBrokerDealerPayload,
    brokerDealerFormValuesFromDetail,
    hasStoredBrokerDealer,
    validateBrokerDealer,
    effectiveEwcClassification,
    hazardousPopsFormValuesFromDetail,
    storedHazardousPopsState,
    validateHazardousPopsConsignment,
    currentLondonReceiptDateTime,
    defaultContainsHazardousFromEwc,
    isLegacyRecordWithoutReceiptCapture,
    isReceiptCaptureRequired,
    receiptCaptureFormValuesFromDetail,
    type EwcRuleFieldState,
    type WasteMovementFormValues,
} from "@/features/wasteMovement";
import type { ISiteOption } from "../../customTypings/customer/Customer";
import type { IWasteMovementDetail, WasteMovementDirection } from "../../customTypings/wasteMovement/WasteMovement";
import type { IWasteMovementEwcDefaultRule } from "../../customTypings/wasteMovement/WasteMovementDefaults";
import { sitesService } from "../../services/sites/sites.service";
import { wasteMovementDefaultsService } from "../../services/wasteMovementDefaults/wasteMovementDefaults.service";
import { wasteMovementsService } from "../../services/wasteMovements/wasteMovements.service";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    editFormBreadcrumbLabel,
    editPageTitle,
    emptyStringToNull,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    loadingPageTitle,
    logErrorForDev,
    NO_CHANGES_TOAST_MESSAGE,
    SectionLabels,
    EntityLabels,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";

dayjs.extend(utc);

function formatMovementMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

export type WasteMovementEditConfig = {
    movementDirection: WasteMovementDirection;
    listTitle: string;
    entityLabel: string;
    listPath: string;
    siteStorageKey: string;
};

function toDateInput(value: string | undefined): string {
    if (!value) return "";
    return value.slice(0, 10);
}

function ruleTargetFields(movementDirection: WasteMovementDirection): (keyof WasteMovementFormValues)[] {
    const base: (keyof WasteMovementFormValues)[] = [
        "regDisposalRecoveryCodeId",
        "municipalFlagId",
        "regPhysicalStateId",
    ];
    if (movementDirection === "Received") {
        return [...base, "degradableFlagId", "regSourceActivityId", "regPreTreatmentId"];
    }
    return base;
}

function WasteMovementDefaultsApplier({
    isNew,
    siteId,
    movementDirection,
    regEwcCodeId,
    ewcStateRef,
    applyingRef,
    onSiteDefaultsReadyChange,
}: {
    isNew: boolean;
    siteId: string;
    movementDirection: WasteMovementDirection;
    regEwcCodeId: string;
    ewcStateRef: MutableRefObject<EwcRuleFieldState>;
    applyingRef: MutableRefObject<boolean>;
    /** False while site defaults are loading; true after resetForm baseline is applied. */
    onSiteDefaultsReadyChange?: (ready: boolean) => void;
}) {
    const { values, setFieldValue, resetForm } = useFormikContext<WasteMovementFormValues>();
    const valuesRef = useRef(values);
    valuesRef.current = values;

    const ewcRulesRef = useRef<IWasteMovementEwcDefaultRule[]>([]);
    const prevEwcRef = useRef<string>("");
    const defaultsLoadedRef = useRef(false);

    useEffect(() => {
        if (!isNew || !siteId) return;
        let cancelled = false;
        defaultsLoadedRef.current = false;
        prevEwcRef.current = "";
        ewcStateRef.current = createEwcRuleFieldState();
        onSiteDefaultsReadyChange?.(false);

        wasteMovementDefaultsService
            .getResolved(siteId, movementDirection)
            .then((resolved) => {
                if (cancelled) return;
                ewcRulesRef.current = resolved.ewcRules;
                applyingRef.current = true;

                const baseline = mergeWasteMovementSiteDefaultsIntoFormValues(
                    valuesRef.current,
                    resolved.prefillValues,
                    resolved.ewcRules,
                    createEwcRuleFieldState()
                );
                ewcStateRef.current = baseline.ewcState;
                if (baseline.values.regEwcCodeId) {
                    prevEwcRef.current = baseline.values.regEwcCodeId;
                }

                resetForm({ values: baseline.values });
                applyingRef.current = false;
                defaultsLoadedRef.current = true;
                onSiteDefaultsReadyChange?.(true);
            })
            .catch((e: unknown) => {
                logErrorForDev(`${movementDirection}.defaults.prefill`, e);
                if (!cancelled) {
                    onSiteDefaultsReadyChange?.(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isNew, siteId, movementDirection, resetForm, ewcStateRef, applyingRef, onSiteDefaultsReadyChange]);

    useEffect(() => {
        if (!isNew || !defaultsLoadedRef.current || applyingRef.current) return;
        if (regEwcCodeId === prevEwcRef.current) return;
        prevEwcRef.current = regEwcCodeId;
        const result = applyEwcDefaultRule(regEwcCodeId, ewcRulesRef.current, ewcStateRef.current);
        ewcStateRef.current = result.nextState;
        applyingRef.current = true;
        for (const [key, val] of Object.entries(result.updates)) {
            void setFieldValue(key, val, false);
        }
        applyingRef.current = false;
    }, [regEwcCodeId, isNew, setFieldValue, ewcStateRef, applyingRef]);

    return null;
}

type WasteMovementEditFormBodyProps = {
    config: WasteMovementEditConfig;
    isNew: boolean;
    sites: ISiteOption[];
    showSiteField: boolean;
    lookups: NonNullable<ReturnType<typeof useWasteMovementReferenceLookups>["lookups"]>;
    formAutoFocusRef: Ref<HTMLFormElement>;
    liveValidationAfterSubmit: boolean;
    submitAttemptedRef: MutableRefObject<boolean>;
    setLiveValidationAfterSubmit: (value: boolean) => void;
    navigate: ReturnType<typeof useNavigate>;
    values: WasteMovementFormValues;
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void;
    setValues: (values: WasteMovementFormValues, shouldValidate?: boolean) => void;
    detail: IWasteMovementDetail | null;
    isSubmitting: boolean;
    errors: import("formik").FormikErrors<WasteMovementFormValues>;
    touched: import("formik").FormikTouched<WasteMovementFormValues>;
    submitCount: number;
    handleSubmit: (e?: FormEvent<HTMLFormElement>) => void;
    leaveAfterSaveRef: MutableRefObject<boolean>;
    saveOutcomeRef: MutableRefObject<LeaveSaveOutcome>;
    readOnlyForm: boolean;
    canMutate: boolean;
};

function WasteMovementEditFormBody({
    config,
    isNew,
    sites,
    showSiteField,
    lookups,
    formAutoFocusRef,
    liveValidationAfterSubmit,
    submitAttemptedRef,
    setLiveValidationAfterSubmit,
    navigate,
    values,
    setFieldValue,
    setValues,
    detail,
    isSubmitting,
    errors,
    touched,
    submitCount,
    handleSubmit,
    leaveAfterSaveRef,
    saveOutcomeRef,
    readOnlyForm,
    canMutate,
}: WasteMovementEditFormBodyProps) {
    const ewcStateRef = useRef<EwcRuleFieldState>(createEwcRuleFieldState());
    const isReceived = config.movementDirection === "Received";
    const legacyWithoutCapture = !isNew && isReceived && isLegacyRecordWithoutReceiptCapture(detail);
    const captureRequired = isReceiptCaptureRequired({ movementDirection: config.movementDirection, isNew, detail, values });
    const carrierLookups = useWasteMovementCarrierLookups(values.siteId, isReceived && !readOnlyForm);
    const hazardousPops = useMemo(
        () => ({
            ewc: effectiveEwcClassification({ isNew, detail, regEwcCodeId: values.regEwcCodeId, ewcs: lookups.ewcs }),
            stored: storedHazardousPopsState(isNew ? null : detail),
        }),
        [isNew, detail, values.regEwcCodeId, lookups.ewcs]
    );
    const shouldValidateOnChange = submitCount > 0 || liveValidationAfterSubmit;
    const applyValues = useCallback(
        (updater: (current: WasteMovementFormValues) => WasteMovementFormValues) => {
            setValues(updater(values), shouldValidateOnChange);
        },
        [setValues, values, shouldValidateOnChange]
    );
    const selectedEwc = useMemo(
        () => lookups.ewcs.find((e) => e.id === values.regEwcCodeId) ?? null,
        [lookups.ewcs, values.regEwcCodeId]
    );
    const prevEwcIdRef = useRef(values.regEwcCodeId);
    useEffect(() => {
        const previous = prevEwcIdRef.current;
        prevEwcIdRef.current = values.regEwcCodeId;
        // Suggest the hazardous answer from the EWC classification only when the selection actually changes on a
        // record that is capturing receipt details; an untouched legacy record and any explicit answer are left alone.
        if (!isReceived || readOnlyForm || !selectedEwc || previous === values.regEwcCodeId || !captureRequired) return;
        const next = defaultContainsHazardousFromEwc(values, selectedEwc);
        if (next !== values) void setFieldValue("containsHazardous", next.containsHazardous, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReceived, readOnlyForm, selectedEwc?.id, values.regEwcCodeId, captureRequired]);
    const applyingDefaultsRef = useRef(false);
    const [siteDefaultsReady, setSiteDefaultsReady] = useState(!isNew);
    const ruleTargets = useMemo(() => ruleTargetFields(config.movementDirection), [config.movementDirection]);

    const handleSiteDefaultsReadyChange = useCallback((ready: boolean) => {
        setSiteDefaultsReady(ready);
    }, []);

    const handleGridFieldChange = useCallback(
        (field: string, value: string, shouldValidate?: boolean) => {
            if (
                !applyingDefaultsRef.current &&
                ruleTargets.includes(field as keyof WasteMovementFormValues)
            ) {
                markFieldUserLocked(ewcStateRef.current, field);
            }
            void setFieldValue(field, value, shouldValidate);
        },
        [ruleTargets, setFieldValue]
    );

    const form = (
        <FormikForm
            ref={formAutoFocusRef}
            noValidate
            onSubmit={(event) => {
                submitAttemptedRef.current = true;
                setLiveValidationAfterSubmit(true);
                handleSubmit(event);
            }}
        >
            {isNew && values.siteId && (
                <WasteMovementDefaultsApplier
                    isNew={isNew}
                    siteId={values.siteId}
                    movementDirection={config.movementDirection}
                    regEwcCodeId={values.regEwcCodeId}
                    ewcStateRef={ewcStateRef}
                    applyingRef={applyingDefaultsRef}
                    onSiteDefaultsReadyChange={handleSiteDefaultsReadyChange}
                />
            )}
            <WasteMovementFormGrid
                movementDirection={config.movementDirection}
                isNew={isNew}
                sites={sites}
                showSiteField={showSiteField}
                values={values}
                errors={errors}
                touched={touched}
                submitCount={submitCount}
                liveValidationAfterSubmit={liveValidationAfterSubmit}
                setFieldValue={handleGridFieldChange}
                lookups={lookups}
                readOnlyForm={readOnlyForm}
                captureRequired={captureRequired}
                hazardousPops={hazardousPops}
                hasStoredBrokerDealer={!isNew && hasStoredBrokerDealer(detail)}
                legacyWithoutCapture={legacyWithoutCapture}
                carrierLookups={carrierLookups}
                applyValues={applyValues}
            />
            <EditFormSplitFooter>
                <EditFormSaveCancelButtons
                    isSubmitting={isSubmitting}
                    saveLabel={isNew ? "Save" : "Save changes"}
                    onCancel={() => navigate(config.listPath)}
                    isNewRecord={isNew}
                    newRecordLabel={wasteMovementDirectionLabel(config.movementDirection)}
                    disabled={readOnlyForm || !canMutate}
                    readOnlyForm={readOnlyForm}
                    enableSaveBeforeLeave={!isNew}
                    leaveAfterSaveRef={leaveAfterSaveRef}
                    saveOutcomeRef={saveOutcomeRef}
                />
            </EditFormSplitFooter>
        </FormikForm>
    );

    return (
        <FormikUnsavedChangesGuard
            enableRouteBlocking
            readOnlyForm={readOnlyForm}
            isNewRecord={isNew}
            newRecordLabel={wasteMovementDirectionLabel(config.movementDirection)}
            enableUnsavedChangesGuard={!isNew || !values.siteId || siteDefaultsReady}
            enableSaveBeforeLeave={!isNew}
            leaveAfterSaveRef={leaveAfterSaveRef}
            saveOutcomeRef={saveOutcomeRef}
        >
            {form}
        </FormikUnsavedChangesGuard>
    );
}

export function WasteMovementEditPage({ config }: { config: WasteMovementEditConfig }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !id || id === "new";
    const currentUser = useAtom(userAtom);
    const roleNames = useMemo(
        () => currentUser?.identity.roles.map((r: IRole) => r.name) ?? [],
        [currentUser?.identity.roles]
    );

    const [sites, setSites] = useState<ISiteOption[]>([]);
    const [sitesLoaded, setSitesLoaded] = useState(false);
    const [detail, setDetail] = useState<IWasteMovementDetail | null>(null);
    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const submitAttemptedRef = useRef(false);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);
    const { lookups, error: lookupError } = useWasteMovementReferenceLookups();

    const resetLiveValidation = () => {
        submitAttemptedRef.current = false;
        setLiveValidationAfterSubmit(false);
    };

    const reportingEligibleSites = useMemo(
        () => (isNew ? filterReportingEligibleSites(sites) : sites),
        [isNew, sites]
    );

    const formAutoFocusRef = useFormAutoFocus<HTMLFormElement>(
        !loading && sitesLoaded && !!lookups && !loadError && !(isNew && reportingEligibleSites.length === 0),
        [loading, sitesLoaded, lookups, detail, isNew, loadError, reportingEligibleSites.length]
    );

    const navigationSiteId = (location.state as { siteId?: string } | null)?.siteId;
    const storedSiteId = typeof window !== "undefined" ? localStorage.getItem(config.siteStorageKey) : null;

    const defaultSiteId = useMemo(() => {
        if (!isNew) return detail?.siteId ?? "";
        const preferred = navigationSiteId ?? storedSiteId;
        return resolveNewWasteMovementSiteId(reportingEligibleSites, preferred);
    }, [isNew, detail?.siteId, navigationSiteId, storedSiteId, reportingEligibleSites]);

    const showSiteField = isNew && reportingEligibleSites.length > 1;

    useEffect(() => {
        sitesService
            .listForOrganisation()
            .then((list) => {
                setSites(list);
                setSitesLoaded(true);
            })
            .catch((e: unknown) => {
                logErrorForDev(`${config.listPath}.edit.sites`, e);
                setSitesLoaded(true);
            });
    }, [config.listPath]);

    useEffect(() => {
        if (isNew) return;
        const siteId = navigationSiteId ?? storedSiteId;
        if (!siteId || !id) {
            setLoadError("Site context is missing. Open this record from the list or use a bookmark with site context.");
            setLoading(false);
            return;
        }
        wasteMovementsService
            .getById(siteId, id, { includeAudit: true })
            .then((row) => {
                setDetail(row);
                setLoadError(null);
            })
            .catch((e: unknown) => setLoadError(getUserFacingErrorMessage(e, "Could not load this record.")))
            .finally(() => setLoading(false));
    }, [isNew, id, navigationSiteId, storedSiteId]);

    const initialValues: WasteMovementFormValues = useMemo(() => {
        // Site time (Europe/London) is the reference for the movement date and receipt time, whatever the browser's zone.
        const londonNow = currentLondonReceiptDateTime();
        return {
            siteId: detail?.siteId ?? defaultSiteId,
            movementDate: toDateInput(detail?.movementDate) || londonNow.date,
            weightTonnes: detail ? String(detail.weightTonnes) : "",
            reference: detail?.reference ?? "",
            regEwcCodeId: detail?.regEwcCodeId ?? "",
            originGeographyId: detail?.originGeographyId ?? "",
            destinationGeographyId: detail?.destinationGeographyId ?? "",
            regDisposalRecoveryCodeId: detail?.regDisposalRecoveryCodeId ?? "",
            regPhysicalStateId: detail?.regPhysicalStateId ?? "",
            municipalFlagId: detail?.municipalFlagId ?? "",
            degradableFlagId: detail?.degradableFlagId ?? "",
            regSourceActivityId: detail?.regSourceActivityId ?? "",
            regPreTreatmentId: detail?.regPreTreatmentId ?? "",
            notes: detail?.notes ?? "",
            ...receiptCaptureFormValuesFromDetail(detail),
            ...hazardousPopsFormValuesFromDetail(detail),
            ...brokerDealerFormValuesFromDetail(detail),
            // A new receipt starts at the current UK wall-clock time; it is visible, editable and sent explicitly.
            ...(isNew && config.movementDirection === "Received"
                ? { receivedTime: londonNow.time, receivedTimeOccurrence: londonNow.occurrence }
                : {}),
        };
    }, [detail, defaultSiteId, isNew, config.movementDirection]);

    const validationSchema = useMemo(() => buildWasteMovementValidationSchema(config.movementDirection), [config.movementDirection]);
    const validationContext = useMemo(() => ({ isNew }), [isNew]);

    const pageTitle = isNew
        ? `New ${config.listTitle} Record`
        : editPageTitle(config.entityLabel, false);

    const formAccess = useMemo(
        () =>
            resolveWasteMovementFormAccess({
                isNew,
                roleNames,
                entityLabel: config.entityLabel,
                detail,
                reportingEligibleSites: reportingEligibleSites,
            }),
        [isNew, roleNames, config.entityLabel, detail, reportingEligibleSites]
    );
    const { readOnlyForm, canMutate, permissionMessage, blockReason } = formAccess;

    if (loading || (!isNew && !detail && !loadError) || (isNew && !sitesLoaded)) {
        return (
            <Container fluid className="py-4">
                <Helmet>
                    <title>{loadingPageTitle(config.entityLabel)}</title>
                </Helmet>
                <LoadingSpinner />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="py-4">
                <Alert variant="danger">{loadError}</Alert>
                <Link to={config.listPath}>Back to {config.listTitle}</Link>
            </Container>
        );
    }

    if (isNew && (blockReason === "role" || blockReason === "noReportingSites")) {
        return (
            <Container fluid className="py-4">
                <Helmet>
                    <title>{pageTitle}</title>
                </Helmet>
                <Alert variant="warning">{permissionMessage}</Alert>
                <Link to={config.listPath}>Back to {config.listTitle}</Link>
            </Container>
        );
    }

    if (!lookups) {
        return (
            <Container fluid className="py-4">
                <Alert variant={lookupError ? "danger" : "info"}>{lookupError ?? "Loading reference data…"}</Alert>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: config.listTitle, to: config.listPath },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />
            <h1 className="mb-3">{pageTitle}</h1>
            {!isNew && detail && (
                <RecordAuditMetaBar
                    formatDate={formatMovementMetaDate}
                    formatByLine={formatByLine}
                    createdAt={detail.createdAtUtc}
                    createdByDisplayName={detail.createdByDisplayName}
                    updatedAt={detail.updatedAtUtc}
                    lastUpdatedByDisplayName={detail.lastUpdatedByDisplayName}
                    isDeleted={detail.isDeleted}
                    isEnabled={detail.isEnabled}
                />
            )}
            {!isNew && detail && (
                <ParentEntityLifecycleBanner
                    entityLabel={config.entityLabel}
                    name={detail.reference?.trim() || detail.ewcCodeSnapshot}
                    entityIsDeleted={detail.isDeleted}
                    entityIsEnabled={detail.isEnabled}
                    parentSiteName={detail.siteName}
                    parentSiteIsDeleted={detail.siteIsDeleted}
                    parentSiteIsEnabled={detail.siteIsEnabled}
                    accessLevel={detail.siteAccessLevel ?? "readWrite"}
                />
            )}
            {!isNew && permissionMessage && (
                <Alert variant={blockReason === "role" ? "info" : "warning"} className="mb-3">
                    {permissionMessage}
                </Alert>
            )}
            {/* S2A/S2B: DEFRA DWT readiness and submission of the saved record (Waste Received only); re-read after each save. */}
            <DwtSubmissionSection
                siteId={detail?.siteId}
                movementId={detail?.id}
                savedVersion={detail?.updatedAtUtc}
                enabled={!isNew && config.movementDirection === "Received" && !!detail}
                canSubmit={canMutate}
            />
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validate={(values) => {
                    const captureRequired = isReceiptCaptureRequired({
                        movementDirection: config.movementDirection,
                        isNew,
                        detail,
                        values,
                    });
                    // S1C rules mirror the server's; they need the EWC classification the save will be checked against.
                    const hazardousPopsErrors = validateHazardousPopsConsignment(values, {
                        movementDirection: config.movementDirection,
                        captureRequired,
                        isNew,
                        ewc: effectiveEwcClassification({ isNew, detail, regEwcCodeId: values.regEwcCodeId, ewcs: lookups.ewcs }),
                        stored: storedHazardousPopsState(isNew ? null : detail),
                    });
                    // S1D broker/dealer rules mirror the server's too.
                    const brokerDealerErrors = validateBrokerDealer(values, {
                        movementDirection: config.movementDirection,
                        captureRequired,
                        isNew,
                        hasStored: hasStoredBrokerDealer(isNew ? null : detail),
                    });
                    const mergeDetailErrors = (errors: Record<string, unknown>) => {
                        for (const [key, message] of [...Object.entries(hazardousPopsErrors), ...Object.entries(brokerDealerErrors)]) {
                            if (message !== undefined && !(key in errors)) errors[key] = message;
                        }
                        return errors;
                    };
                    return validationSchema
                        .validate(values, {
                            abortEarly: false,
                            context: { ...validationContext, captureRequired },
                        })
                        .then(() => mergeDetailErrors({}))
                        .catch((err: { inner?: { path?: string; message: string }[] }) => {
                            const errors: Record<string, unknown> = {};
                            for (const inner of err.inner ?? []) {
                                if (inner.path && !(inner.path in errors)) errors[inner.path] = inner.message;
                            }
                            return mergeDetailErrors(errors);
                        });
                }}
                validateOnMount={false}
                validateOnBlur={false}
                validateOnChange={liveValidationAfterSubmit || submitAttemptedRef.current}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    if (readOnlyForm) return;
                    const siteId = isNew ? values.siteId : detail!.siteId;
                    if (!siteId) {
                        setSubmitting(false);
                        showToastError("Site is required.");
                        return;
                    }
                    saveOutcomeRef.current = null;
                    const payload = {
                        movementDirection: config.movementDirection,
                        movementDate: values.movementDate,
                        weightTonnes: Number(values.weightTonnes),
                        regEwcCodeId: values.regEwcCodeId,
                        originGeographyId: emptyStringToNull(values.originGeographyId),
                        destinationGeographyId: emptyStringToNull(values.destinationGeographyId),
                        regDisposalRecoveryCodeId: values.regDisposalRecoveryCodeId,
                        regPhysicalStateId: values.regPhysicalStateId,
                        municipalFlagId: values.municipalFlagId,
                        degradableFlagId: emptyStringToNull(values.degradableFlagId),
                        regSourceActivityId: emptyStringToNull(values.regSourceActivityId),
                        regPreTreatmentId: emptyStringToNull(values.regPreTreatmentId),
                        reference: emptyStringToNull(values.reference),
                        notes: emptyStringToNull(values.notes),
                        ...buildReceiptCapturePayload(values, config.movementDirection),
                        ...buildHazardousPopsPayload(values, config.movementDirection, { isNew, detail }),
                        ...buildBrokerDealerPayload(values, config.movementDirection, { isNew, detail }),
                    };
                    try {
                        if (isNew) {
                            localStorage.setItem(config.siteStorageKey, siteId);
                            const created = await wasteMovementsService.create(siteId, payload);
                            resetLiveValidation();
                            showToastSuccess("Record saved.");
                            navigate(`${config.listPath}/${created.id}`, { replace: true, state: { siteId } });
                            return;
                        }
                        const result = await wasteMovementsService.update(siteId, { ...payload, id: detail!.id });
                        if (isUnchangedUpdateResponse(result)) {
                            resetLiveValidation();
                            saveOutcomeRef.current = "unchanged";
                            showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                            if (leaveAfterSaveRef.current) return;
                            return;
                        }
                        resetLiveValidation();
                        saveOutcomeRef.current = "success";
                        showToastSuccess("Record saved.");
                        resetForm({ values });
                        if (leaveAfterSaveRef.current) return;
                        setDetail(result as IWasteMovementDetail);
                    } catch (e: unknown) {
                        saveOutcomeRef.current = "failure";
                        showToastError(getUserFacingErrorMessage(e, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, setFieldValue, setValues, isSubmitting, errors, touched, submitCount, handleSubmit }) => (
                    <WasteMovementEditFormBody
                        config={config}
                        isNew={isNew}
                        sites={reportingEligibleSites}
                        showSiteField={showSiteField}
                        lookups={lookups}
                        formAutoFocusRef={formAutoFocusRef}
                        liveValidationAfterSubmit={liveValidationAfterSubmit}
                        submitAttemptedRef={submitAttemptedRef}
                        setLiveValidationAfterSubmit={setLiveValidationAfterSubmit}
                        navigate={navigate}
                        values={values}
                        setFieldValue={setFieldValue}
                        setValues={setValues}
                        detail={detail}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                        handleSubmit={handleSubmit}
                        leaveAfterSaveRef={leaveAfterSaveRef}
                        saveOutcomeRef={saveOutcomeRef}
                        readOnlyForm={readOnlyForm}
                        canMutate={canMutate}
                    />
                )}
            </Formik>
        </Container>
    );
}
